'use client'

import { useLocale, useTranslations } from 'next-intl'
import { type ReactNode, useCallback } from 'react'

import {
  LeaderboardBoard,
  LeaderboardLoading
} from '@/features/leaderboard/components/LeaderboardBoard'
import { LeaderboardLoadError } from '@/features/leaderboard/components/LeaderboardLoadError'
import { formatLeaderboardNumber } from '@/features/leaderboard/formatLeaderboardNumber'
import { usePagedLeaderboard } from '@/features/leaderboard/usePagedLeaderboard'

import { fetchClubsLeaderboardPage } from '../api'
import type { ClubLeaderboardRow } from '../types'
import { useClubsLeaderboardRank } from '../useClubsLeaderboardRank'
import { ClubCrest, ClubRow, ClubRowHeader } from './ClubRow'

const SELF_RANGE_RADIUS = 10

type ClubsBoardProps = {
  /** The viewer's club: their own club account or their Favorite Club. */
  myClubId?: string
  sportType?: string
  search?: string
  emptyState: ReactNode
}

export function ClubsBoard({
  myClubId,
  sportType,
  search,
  emptyState
}: ClubsBoardProps) {
  const t = useTranslations('leaderboardBoard')
  // A search shows only the matches: no pinned row, no jump to a page.
  const selfRank = useClubsLeaderboardRank(
    search ? undefined : myClubId,
    sportType
  )

  // BAT-860: the club's sport comes in the page's language, so the language
  // is part of the request and of the cache key.
  const locale = useLocale()
  const fetchPage = useCallback(
    (page: number, pageSize: number) =>
      fetchClubsLeaderboardPage({ sportType, search }, page, pageSize, locale),
    [sportType, search, locale]
  )
  const isSelfRow = useCallback(
    (row: ClubLeaderboardRow) => row.clubId === myClubId,
    [myClubId]
  )

  const board = usePagedLeaderboard({
    queryKey: [
      'clubs_leaderboard_page',
      sportType ?? 'all',
      search ?? '',
      locale
    ],
    fetchPage,
    selfRank,
    isSelfRow: myClubId ? isSelfRow : undefined
  })

  if (board.isLoading) {
    return <LeaderboardLoading />
  }

  if (board.isError) {
    return <LeaderboardLoadError onRetry={board.retry} />
  }

  if (board.blocks.length === 0) {
    return emptyState
  }

  const self = board.selfRow?.row

  return (
    <LeaderboardBoard
      header={<ClubRowHeader />}
      blocks={board.blocks}
      getRowKey={row => row.clubId}
      renderRow={(row, state) => (
        <ClubRow
          row={row}
          isSelf={state.isSelf}
          highlighted={state.highlighted}
        />
      )}
      isSelfRow={myClubId ? isSelfRow : undefined}
      selfRow={board.selfRow}
      selfLabel={t('yourPosition')}
      pill={
        self
          ? {
              visual: <ClubCrest row={self} />,
              label: t('myRank.label'),
              rank: self.rank,
              value: formatLeaderboardNumber(self.totalViews),
              unit: t('myRank.views')
            }
          : undefined
      }
      onRevealSelf={
        selfRank
          ? () => board.revealRank(selfRank, SELF_RANGE_RADIUS)
          : undefined
      }
      hasMore={board.hasMore}
      isFetchingMore={board.isFetchingMore}
      onLoadMore={board.loadMore}
      onLoadPrevious={board.loadPrevious}
      labels={{
        loadMore: t('loadMore'),
        showHigher: t('showHigher'),
        jump: t('myRank.jump')
      }}
    />
  )
}
